// src/components/SpouseHubView.jsx
//
// The spouse / partner home (resolveSurface() === 'family', memberType: spouse).
// Until now this cohort only had FamilyHuddleView (the shared family ritual) and
// no wellbeing surface of their own — see docs/eri-hub-member-funnel-spec.md §5.
//
// This surface keeps the Family Huddle as a real section and adds the three
// things the spec calls out for this persona:
//   1. Caregiver Wellbeing — looking after yourself while you support a partner.
//   2. Your Own Check-In — a reflective pulse of THEIR own, not the member's.
//   3. Family Visibility — a self-set "family snapshot" to bring into the huddle.
//
// Design notes (mirrors TeenHubView): content is static + self-contained, and the
// check-in / snapshot are REFLECTIVE tools — they never score or label. They
// persist privately to users/{uid}/private_wellbeing/spouse (self-only Firestore
// rules); without a signed-in uid (e.g. preview mode) they stay local-state only.
// A real partner assessment + true cross-member family visibility (reading other
// members' data, shared appropriately) is a separate, larger lift flagged in the spec.

import React, { useState, useEffect } from 'react';
import {
    HeartHandshake, Activity, ChevronDown, ArrowRight,
    Battery, Clock, ShieldAlert, CloudSun, Cloud, CloudRain, Sun, CheckCircle2,
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import FamilyHuddleView from './FamilyHuddleView';
import SupportOutlet from './dashboard/SupportOutlet';
import { FaceScale, CheckInResults, StreakHeader, checkedInThisWeek, computeStreak } from './shared/CheckInScale';

// --- CAREGIVER WELLBEING ---
// Support for the load of supporting someone. Expandable, like the teen pillars.
const CAREGIVER_CARDS = [
    {
        id: 'empty-cup',
        icon: Battery,
        title: 'You Can’t Pour From an Empty Cup',
        hook: 'Looking after yourself isn’t selfish — it’s the job.',
        body: 'When you’re supporting a partner through transition or brain-health changes, it’s easy to put yourself last for months at a time. But your steadiness is the thing they lean on, and it runs on a battery that needs charging too.',
        tries: ['Protect one thing a week that’s just yours', 'Say yes to help when it’s offered', 'Lower the bar on the non-essentials — guilt-free'],
    },
    {
        id: 'support-not-fix',
        icon: HeartHandshake,
        title: 'Supporting, Not Fixing',
        hook: 'You don’t have to solve it to be there for it.',
        body: 'The pressure to “fix” a partner’s hard days can be exhausting and rarely lands. Often the most useful thing is simply being present — listening without rushing to a solution carries more than advice does.',
        tries: ['Ask “do you want help, or to be heard?”', 'Let silence sit — it’s not yours to fill', 'Their recovery is theirs; your job is the company'],
    },
    {
        id: 'oxygen',
        icon: Clock,
        title: 'Your Own Oxygen Mask',
        hook: 'Recovery isn’t a reward — it’s maintenance.',
        body: 'Sleep, movement, and your own connections aren’t luxuries you earn after everyone else is sorted. They’re what keep you able to show up. Scheduling them is as legitimate as any appointment on the calendar.',
        tries: ['Book recovery like you’d book anything else', 'Keep one friendship alive outside the household', 'Move your body — even ten minutes resets a lot'],
    },
    {
        id: 'burnout',
        icon: ShieldAlert,
        title: 'Watch for the Quiet Burnout',
        hook: 'Caregiver burnout creeps; it rarely announces itself.',
        body: 'Constant tiredness, resentment you didn’t used to feel, pulling away from people, or feeling numb — these are signs the load has tipped, not signs you’re failing. Naming it early makes it far easier to recover from.',
        tries: ['Notice resentment without judging it', 'Tell one person the true version of how you are', 'Reaching for support is strength, not surrender'],
    },
];

// --- YOUR OWN CHECK-IN (reflective, never scored) ---
// Faces follow each question's own low→high meaning (not a generic sad→happy
// scale) — note worry runs light→heavy, so `higherIsBetter: false`.
const CHECK_IN_PROMPTS = [
    {
        id: 'energy', label: 'How are your own energy reserves?', low: 'Running low', high: 'Full', higherIsBetter: true,
        faces: [
            { emoji: '😫', word: 'Drained' }, { emoji: '😪', word: 'Low' }, { emoji: '😐', word: 'OK' },
            { emoji: '🙂', word: 'Good' }, { emoji: '😄', word: 'Full' },
        ],
    },
    {
        id: 'supported', label: 'How supported do you feel yourself?', low: 'On my own', high: 'Held up', higherIsBetter: true,
        faces: [
            { emoji: '🙁', word: 'Alone' }, { emoji: '😕', word: 'Barely' }, { emoji: '😐', word: 'Some' },
            { emoji: '🙂', word: 'Backed' }, { emoji: '🤗', word: 'Held' },
        ],
    },
    {
        id: 'time', label: 'How much time is just for you?', low: 'None', high: 'Plenty', higherIsBetter: true,
        faces: [
            { emoji: '😵', word: 'None' }, { emoji: '😕', word: 'Scraps' }, { emoji: '😐', word: 'Some' },
            { emoji: '🙂', word: 'Decent' }, { emoji: '😌', word: 'Plenty' },
        ],
    },
    {
        id: 'worry', label: 'How heavy is the worry right now?', low: 'Light', high: 'Heavy', higherIsBetter: false,
        faces: [
            { emoji: '😌', word: 'Light' }, { emoji: '🙂', word: 'Low' }, { emoji: '😐', word: 'Some' },
            { emoji: '😟', word: 'High' }, { emoji: '😫', word: 'Heavy' },
        ],
    },
];

const reflectionFor = (a) => {
    const energy = a.energy ?? 3;
    const supported = a.supported ?? 3;
    const time = a.time ?? 3;
    const worry = a.worry ?? 3;

    if (energy <= 2 && worry >= 4) {
        return 'Low reserves and heavy worry together is the classic caregiver squeeze — and a real signal to put some of that support back toward yourself this week. The Caregiver Wellbeing cards above are written for exactly this.';
    }
    if (supported <= 2) {
        return 'It sounds like you’re holding a lot without much holding you up. That’s the part that quietly wears people down. Telling one person the honest version of how you are tends to shift it more than anything else.';
    }
    if (time <= 2) {
        return 'There’s almost nothing on the calendar that’s just yours right now. That’s common when you’re the steady one — and worth gently changing. Even one protected hour a week counts as maintenance, not indulgence.';
    }
    if (energy >= 4 && worry <= 2) {
        return 'You’re in a steadier stretch — that’s worth noticing and protecting. Knowing what’s working now makes the harder weeks easier to ride out.';
    }
    return 'Thank you for turning the lens on yourself for a minute. There’s no score here — just you, checking that the person doing the supporting is also okay. That matters.';
};

// Gentle relative date for the "last check-in / last snapshot" lines.
const formatCheckinDate = (iso) => {
    const then = new Date(iso);
    if (isNaN(then.getTime())) return '';
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// --- FAMILY VISIBILITY ---
// Honest framing: this is the partner's own read of the household this week,
// meant to spark the Family Huddle conversation — not data pulled from other
// members (that's a separate, larger feature).
const FAMILY_WEATHER = [
    { id: 'sunny', icon: Sun, label: 'Sunny', note: 'Connected and steady', color: 'text-amber-500', bg: 'bg-amber-50', ring: 'ring-amber-300' },
    { id: 'partly', icon: CloudSun, label: 'Mixed', note: 'Good days and harder ones', color: 'text-sky-500', bg: 'bg-sky-50', ring: 'ring-sky-300' },
    { id: 'cloudy', icon: Cloud, label: 'Cloudy', note: 'A bit flat or distant', color: 'text-slate-500', bg: 'bg-slate-100', ring: 'ring-slate-300' },
    { id: 'stormy', icon: CloudRain, label: 'Stormy', note: 'Tense — needs care', color: 'text-indigo-500', bg: 'bg-indigo-50', ring: 'ring-indigo-300' },
];

// --- EXPANDABLE CAREGIVER CARD ---
const CaregiverCard = ({ card }) => {
    const [open, setOpen] = useState(false);
    const Icon = card.icon;
    return (
        <div className={`bg-white rounded-2xl border border-slate-200 overflow-hidden transition-shadow ${open ? 'shadow-lg' : 'shadow-sm hover:shadow-md'}`}>
            <button onClick={() => setOpen(o => !o)} className="w-full p-5 flex items-center gap-4 text-left">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-rose-50 text-rose-600">
                    <Icon size={24} />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-black text-slate-900 leading-tight">{card.title}</h3>
                    <p className="text-sm text-slate-500">{card.hook}</p>
                </div>
                <ChevronDown size={20} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="px-5 pb-5 pt-1 animate-in slide-in-from-top-2 duration-300">
                    <p className="text-slate-600 leading-relaxed mb-4">{card.body}</p>
                    <div className="space-y-2">
                        {card.tries.map((t, i) => (
                            <div key={i} className="flex items-start gap-3 text-sm text-slate-700">
                                <ArrowRight size={16} className="text-rose-500 shrink-0 mt-0.5" />
                                <span>{t}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const SpouseHubView = ({ currentUser }) => {
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [weather, setWeather] = useState(null);
    // Saved private history (self-only Firestore rules, never scored).
    const [checkins, setCheckins] = useState([]);
    const [lastSnapshot, setLastSnapshot] = useState(null);

    const allAnswered = CHECK_IN_PROMPTS.every(p => answers[p.id] != null);

    // Load saved check-ins + snapshot from the private doc. Without a uid
    // (e.g. preview mode) this surface stays local-only.
    useEffect(() => {
        if (!currentUser?.uid) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', currentUser.uid, 'private_wellbeing', 'spouse'));
                if (cancelled || !snap.exists()) return;
                const data = snap.data();
                if (Array.isArray(data.checkins)) {
                    setCheckins(data.checkins);
                    // Already checked in within the last 7 days? Land on the
                    // unlocked reflection (showing that entry), not a blank form.
                    if (checkedInThisWeek(data.checkins)) {
                        const last = data.checkins[data.checkins.length - 1];
                        if (last?.answers) setAnswers(last.answers);
                        setSubmitted(true);
                    }
                }
                if (data.snapshot?.weather) {
                    setLastSnapshot(data.snapshot);
                    setWeather(data.snapshot.weather);
                }
            } catch (e) {
                console.error('Error loading spouse hub data:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUser?.uid]);

    const handleSubmit = async () => {
        setSubmitted(true);
        const entry = { date: new Date().toISOString(), answers: { ...answers } };
        const next = [...checkins, entry].slice(-30); // keep the most recent 30
        setCheckins(next); // local history always includes this session (results screen reads it)
        if (!currentUser?.uid) return; // preview mode: local-state only, nothing persisted
        try {
            await setDoc(
                doc(db, 'users', currentUser.uid, 'private_wellbeing', 'spouse'),
                { checkins: next, updatedAt: new Date().toISOString() },
                { merge: true }
            );
        } catch (e) {
            console.error('Error saving spouse check-in:', e);
        }
    };

    const handleWeatherSelect = async (id) => {
        setWeather(id);
        if (!currentUser?.uid) return;
        const snapshot = { weather: id, date: new Date().toISOString() };
        setLastSnapshot(snapshot);
        try {
            await setDoc(
                doc(db, 'users', currentUser.uid, 'private_wellbeing', 'spouse'),
                { snapshot, updatedAt: new Date().toISOString() },
                { merge: true }
            );
        } catch (e) {
            console.error('Error saving family snapshot:', e);
        }
    };

    const lastCheckin = checkins.length > 0 ? checkins[checkins.length - 1] : null;
    // Weekly pulse framing — derived from history only, never scored.
    const weekDone = checkedInThisWeek(checkins);
    const streak = computeStreak(checkins);
    const lastSnapshotLabel = lastSnapshot
        ? (FAMILY_WEATHER.find(w => w.id === lastSnapshot.weather)?.label || lastSnapshot.weather)
        : null;

    return (
        <div className="max-w-4xl mx-auto space-y-10 animate-in fade-in pb-20">

            {/* HERO */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2 font-bold uppercase tracking-widest text-xs" style={{ color: 'var(--brand-accent)' }}>
                        <Activity size={14} /> Family &amp; Partner Hub
                    </div>
                    <h1 className="text-3xl md:text-4xl font-black mb-4">Supporting them — and you.</h1>
                    <p className="text-slate-300 text-lg leading-relaxed max-w-2xl">
                        Your home as a partner: the family ritual you already have, plus wellbeing tools that are yours alone.
                    </p>
                    <div className="mt-6">
                        <SupportOutlet currentUser={currentUser} />
                    </div>
                </div>
            </div>

            {/* DASHBOARD STATUS — an at-a-glance read of your own week */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${weekDone ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {weekDone ? <CheckCircle2 size={20} /> : <Clock size={20} />}
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Your check-in</p>
                        <p className="font-black text-slate-800 text-sm">{weekDone ? 'Done this week' : 'Due this week'}</p>
                        <p className="text-[11px] text-slate-400">{streak > 0 ? `${streak} week streak` : 'Start a streak'}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-sky-50 text-sky-600"><CloudSun size={20} /></div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Family snapshot</p>
                        <p className="font-black text-slate-800 text-sm">{lastSnapshotLabel || 'Not set yet'}</p>
                        <p className="text-[11px] text-slate-400">{lastSnapshot ? formatCheckinDate(lastSnapshot.date) : 'set it below'}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-rose-50 text-rose-600"><HeartHandshake size={20} /></div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Check-ins</p>
                        <p className="font-black text-slate-800 text-sm">{checkins.length} logged</p>
                        <p className="text-[11px] text-slate-400">private to you</p>
                    </div>
                </div>
            </div>

            {/* CAREGIVER WELLBEING */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1 px-1">Caregiver wellbeing</h2>
                <p className="text-slate-500 text-sm mb-4 px-1">For the person doing the supporting. Tap any one to open it.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CAREGIVER_CARDS.map(c => <CaregiverCard key={c.id} card={c} />)}
                </div>
            </section>

            {/* YOUR OWN CHECK-IN */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 px-1">Your own check-in</h2>
                <StreakHeader streak={streak} status={weekDone ? 'done' : 'due'} accent="rose" />
                {lastCheckin && (
                    <p className="text-sm text-slate-500 mb-3 px-1">
                        Last check-in: <span className="font-bold text-slate-600">{formatCheckinDate(lastCheckin.date)}</span>
                        {' · '}{checkins.length} check-in{checkins.length === 1 ? '' : 's'} so far — checking on yourself counts too.
                    </p>
                )}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
                    {!submitted ? (
                        <>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                Do this week’s check-in to unlock your reflection. A pulse of your own — not the person you support. Four taps, no score, private to you — never shared.
                            </p>
                            <div className="space-y-8">
                                {CHECK_IN_PROMPTS.map(p => (
                                    <FaceScale
                                        key={p.id}
                                        prompt={p}
                                        value={answers[p.id]}
                                        onChange={(v) => setAnswers(a => ({ ...a, [p.id]: v }))}
                                        accent="rose"
                                    />
                                ))}
                            </div>
                            <button
                                onClick={handleSubmit}
                                disabled={!allAnswered}
                                className="w-full mt-8 py-4 bg-slate-900 text-white rounded-2xl font-black hover:bg-black transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                See my reflection <ArrowRight size={18} />
                            </button>
                            {!allAnswered && (
                                <p className="text-center text-xs text-slate-400 mt-3">Tap a face for each one to continue.</p>
                            )}
                        </>
                    ) : (
                        <CheckInResults
                            prompts={CHECK_IN_PROMPTS}
                            answers={answers}
                            history={checkins}
                            reflection={reflectionFor(answers)}
                            onStartOver={() => { setSubmitted(false); setAnswers({}); }}
                            accent="rose"
                        />
                    )}
                </div>
            </section>

            {/* FAMILY VISIBILITY */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1 px-1">Family snapshot</h2>
                <p className="text-slate-500 text-sm mb-4 px-1">Your read on how home feels this week — a starting point to bring into the huddle.</p>
                {lastSnapshot && (
                    <p className="text-sm text-slate-500 mb-3 px-1">
                        Your last snapshot: <span className="font-bold text-slate-600">{lastSnapshotLabel}</span>, {formatCheckinDate(lastSnapshot.date)}
                    </p>
                )}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {FAMILY_WEATHER.map(w => {
                            const Icon = w.icon;
                            const active = weather === w.id;
                            return (
                                <button
                                    key={w.id}
                                    onClick={() => handleWeatherSelect(w.id)}
                                    className={`rounded-2xl border-2 p-4 flex flex-col items-center text-center gap-2 transition-all ${active ? `${w.bg} border-transparent ring-2 ${w.ring} scale-[1.03] shadow-md` : 'bg-white border-slate-200 hover:border-slate-300'}`}
                                >
                                    <Icon size={28} className={active ? w.color : 'text-slate-400'} />
                                    <span className="font-black text-slate-800 text-sm">{w.label}</span>
                                    <span className="text-[11px] text-slate-500 leading-snug">{w.note}</span>
                                </button>
                            );
                        })}
                    </div>
                    {weather && (
                        <div className="mt-6 p-5 bg-slate-50 rounded-2xl border border-slate-200 animate-in fade-in flex items-start gap-3">
                            <CloudSun size={18} className="text-slate-400 shrink-0 mt-0.5" />
                            <p className="text-sm text-slate-600 leading-relaxed">
                                This is your read — it doesn’t pull from anyone else’s account. If it’s anything other than sunny, the Family Huddle below is a good, low-pressure place to talk about why.
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* EXISTING SECTION: FAMILY HUDDLE */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 px-1">Family huddle</h2>
                <FamilyHuddleView currentUser={currentUser} />
            </section>

        </div>
    );
};

export default SpouseHubView;
